// src/pages/AdhesionPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import "../styles/AdhesionPage.css";

export default function AdhesionPage() {
  const location = useLocation();

  const [adhesionAmount] = useState("20.00");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState("");

  // Infos utilisateur
  const [userInfo, setUserInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    tenant_number: "",
    landlord_id: "",
    password: "",
    confirmPassword: "",
  });

  // 🔧 Constantes API (à adapter si besoin pour dev / prod)
  const API_BASE_URL = "https://api.nms06.com/api";
  const CREATE_ADHESION_URL = `${API_BASE_URL}/paypal/create-adhesion`;
  const CAPTURE_PAYMENT_URL = `${API_BASE_URL}/paypal/capture-payment`;
  const EMAIL_API_URL = `${API_BASE_URL}/send-confirmation-email`;
  const PAYPAL_CLIENT_ID_URL = `${API_BASE_URL}/paypal/client-id`;

  // 🔑 Client ID PayPal
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [isLoadingPaypal, setIsLoadingPaypal] = useState(true);

  // Pré-remplissage via location.state (ex: données venant d’une page précédente)
  useEffect(() => {
    if (location.state && typeof location.state === "object") {
      setUserInfo((prev) => ({
        ...prev,
        ...location.state,
      }));
    }
  }, [location.state]);

  // Récupérer le client-id PayPal
  useEffect(() => {
    const fetchClientId = async () => {
      try {
        const res = await fetch(PAYPAL_CLIENT_ID_URL);
        const data = await res.json();

        if (!res.ok || !data.clientId) {
          throw new Error(
            data?.error || "Client ID PayPal introuvable sur le serveur."
          );
        }

        setPaypalClientId(data.clientId);
      } catch (err) {
        console.error("❌ Erreur chargement client-id PayPal :", err);
        setMessage(
          "Impossible de charger PayPal pour le moment. Veuillez réessayer plus tard."
        );
      } finally {
        setIsLoadingPaypal(false);
      }
    };

    fetchClientId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // pas besoin de le relancer si l’URL ne change pas

  // Options PayPal mémorisées
  const paypalOptions = useMemo(() => {
    if (!paypalClientId) return null;
    return {
      "client-id": paypalClientId,
      currency: "EUR",
    };
  }, [paypalClientId]);

  // Liste de champs pour le formulaire
  const formFields = [
    { key: "first_name", label: "Prénom", type: "text", required: true },
    { key: "last_name", label: "Nom", type: "text", required: true },
    { key: "email", label: "Email", type: "email", required: true },
    { key: "phone", label: "Téléphone", type: "text", required: false },
    { key: "address", label: "Adresse", type: "text", required: false },
    { key: "postal_code", label: "Code postal", type: "text", required: false },
    { key: "city", label: "Ville", type: "text", required: false },
    {
      key: "tenant_number",
      label: "Numéro de locataire (facultatif)",
      type: "text",
      required: false,
    },
    {
      key: "landlord_id",
      label: "ID Propriétaire (facultatif)",
      type: "text",
      required: false,
    },
    { key: "password", label: "Mot de passe", type: "password", required: true },
    {
      key: "confirmPassword",
      label: "Confirmer le mot de passe",
      type: "password",
      required: true,
    },
  ];

  // Champs obligatoires côté JS (logique métier)
  const requiredFieldsKeys = [
    "first_name",
    "last_name",
    "email",
    "password",
    "confirmPassword",
  ];

  // Création d’une commande PayPal
  const createOrderCallback = async () => {
    setIsSubmitting(true);
    setMessage("");

    try {
      // Vérification mots de passe
      if (!userInfo.password || userInfo.password !== userInfo.confirmPassword) {
        const errMsg = "❌ Les mots de passe ne correspondent pas.";
        setMessage(errMsg);
        throw new Error(errMsg);
      }

      // Vérification des champs obligatoires
      const labelsByKey = formFields.reduce((acc, f) => {
        acc[f.key] = f.label;
        return acc;
      }, {});
      const missingFields = requiredFieldsKeys.filter((field) => !userInfo[field]);
      if (missingFields.length > 0) {
        const list = missingFields.map((f) => labelsByKey[f] || f).join(", ");
        const errMsg = `❌ Veuillez remplir : ${list}`;
        setMessage(errMsg);
        throw new Error(errMsg);
      }

      const orderData = {
        adhesion_amount: adhesionAmount,
        ...userInfo,
      };

      console.log("🟢 Envoi des données à PayPal (create-adhesion) :", orderData);

      const response = await fetch(CREATE_ADHESION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData),
      });

      const rawText = await response.text();
      console.log("📥 Réponse brute /paypal/create-adhesion :", rawText);

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${rawText}`);
      }

      let result;
      try {
        result = JSON.parse(rawText);
      } catch (e) {
        console.error("❌ Réponse NON JSON du backend PayPal :", rawText);
        throw new Error("Réponse invalide du serveur de paiement (pas du JSON).");
      }

      if (!result.id) {
        console.error("❌ JSON reçu mais sans id :", result);
        throw new Error("Le serveur n'a pas renvoyé d'identifiant de commande.");
      }

      console.log("✅ ID de commande PayPal reçu :", result.id);
      return result.id;
    } catch (error) {
      console.error("❌ Erreur lors de la création de la commande :", error);
      setMessage(`Erreur : ${error.message}`);
      throw error; // pour que PayPal déclenche onError
    } finally {
      setIsSubmitting(false);
    }
  };

  // Callback après approbation du paiement
  const onApproveCallback = async (data) => {
    console.log("✅ Paiement approuvé (callback PayPal) :", data);

    try {
      if (!userInfo || !userInfo.email) {
        console.error("❌ Erreur : L'email utilisateur est manquant !");
        setMessage("Une erreur est survenue : l'email est manquant.");
        return;
      }

      const capturePayload = {
        orderId: data.orderID,
        formEmail: userInfo.email,
        formFirstName: userInfo.first_name,
        formLastName: userInfo.last_name,
      };

      console.log("📌 Payload pour `/paypal/capture-payment` :", capturePayload);

      const captureResponse = await fetch(CAPTURE_PAYMENT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(capturePayload),
      });

      const captureRaw = await captureResponse.text();
      console.log("📥 Réponse brute /paypal/capture-payment :", captureRaw);

      if (!captureResponse.ok) {
        throw new Error(
          `Échec de la capture du paiement. Statut HTTP : ${captureResponse.status} - ${captureRaw}`
        );
      }

      let captureData;
      try {
        captureData = JSON.parse(captureRaw);
      } catch (e) {
        throw new Error(
          `Réponse JSON invalide de /paypal/capture-payment : ${captureRaw}`
        );
      }

      console.log("✅ Paiement capturé avec succès :", captureData);

      const transactionId = captureData.transactionId || data.orderID || "Inconnu";
      console.log("🔹 ID de transaction récupéré :", transactionId);

      const emailPayload = {
        email: userInfo.email,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        adhesionAmount,
        transactionId,
      };

      console.log("📨 Envoi de l'email avec les données :", emailPayload);

      const emailResponse = await fetch(EMAIL_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailPayload),
      });

      const emailResponseText = await emailResponse.text();
      console.log("🔹 Réponse brute de l'API email :", emailResponseText);

      let emailData;
      try {
        emailData = JSON.parse(emailResponseText);
      } catch (jsonError) {
        throw new Error(`Réponse email invalide : ${emailResponseText}`);
      }

      if (!emailResponse.ok) {
        throw new Error(
          `Échec de l'envoi de l'email. Statut: ${emailResponse.status}, Message: ${
            emailData?.message || "Inconnu"
          }`
        );
      }

      console.log("📧 Email de confirmation envoyé avec succès !");
      setIsModalOpen(true);
    } catch (error) {
      console.error("❌ Erreur lors du processus de paiement :", error.message);
      setMessage(`Erreur lors du paiement : ${error.message}`);
    }
  };

  return (
    <div className="adhesion-page">
      <h2>Adhérez à l'association NMS 06</h2>

      <div className="adhesion-container adhesion-explainer-and-form">
        {/* ✅ Colonne gauche : explication */}
        <div className="adhesion-left">
          <h3>Pourquoi votre adhésion est essentielle ?</h3>

          <p>
            Chaque jour, NMS 06 défend des familles, des locataires, des
            propriétaires et des habitants confrontés à des situations parfois
            très difficiles : logements insalubres, absence de chauffage,
            procédures abusives, erreurs administratives, litiges avec des
            bailleurs, ou tout simplement besoin d’être accompagnés.
          </p>

          <p>
            Pour agir efficacement et défendre vos droits, nous avons besoin de
            moyens. Votre adhésion permet à l’association de financer ce qui
            fait concrètement la différence :
          </p>

          <ul>
            <li>📌 La consultation d’avocats spécialisés pour vos dossiers</li>
            <li>
              📌 Les interventions d’huissiers quand il faut des preuves
              officielles
            </li>
            <li>
              📌 Les frais administratifs nécessaires pour suivre vos
              réclamations et procédures
            </li>
            <li>
              📌 Les outils numériques d’aide, d’accompagnement et de suivi que
              nous mettons à votre disposition
            </li>
            <li>
              📌 La médiation, l’accompagnement collectif et la défense de vos
              droits face aux institutions
            </li>
          </ul>

          <p>
            <strong>
              Avec seulement 20€, vous donnez à NMS 06 les moyens de vous
              défendre, de faire bouger les lignes, et de ne jamais laisser
              quelqu’un seul face à une injustice.
            </strong>
          </p>

          <p>
            Adhérer, c’est soutenir un mouvement citoyen indépendant, au service
            des habitants, et donner plus de poids à votre propre voix.
          </p>
        </div>

        {/* ✅ Colonne droite : formulaire + PayPal */}
        <div className="adhesion-right">
          <form className="adhesion-form" onSubmit={(e) => e.preventDefault()}>
            {formFields.map(({ key, label, type, required }) => (
              <div className="form-group" key={key}>
                <label htmlFor={key}>{label}</label>
                <input
                  id={key}
                  type={type}
                  value={userInfo[key] || ""}
                  onChange={(e) =>
                    setUserInfo((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  required={required}
                />
              </div>
            ))}

            <p className="total-display">
              Montant de l'adhésion : <strong>{adhesionAmount} €</strong>
            </p>

            {message && <p className="message">{message}</p>}

            {/* Chargement PayPal */}
            {isLoadingPaypal && (
              <p
                style={{
                  textAlign: "center",
                  marginTop: "10px",
                  color: "#666",
                }}
              >
                Chargement de PayPal…
              </p>
            )}

            {/* Boutons PayPal */}
            {!isLoadingPaypal && paypalOptions && (
              <PayPalScriptProvider options={paypalOptions}>
                <PayPalButtons
                  createOrder={createOrderCallback}
                  onApprove={onApproveCallback}
                  style={{ layout: "vertical" }}
                  disabled={isSubmitting}
                  onError={(err) => {
                    console.error("❌ Erreur PayPal (SDK) :", err);
                    setMessage(
                      "Une erreur est survenue avec PayPal. Veuillez réessayer."
                    );
                  }}
                />
              </PayPalScriptProvider>
            )}
          </form>
        </div>
      </div>

      {/* Modal de confirmation */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Paiement réussi ! 🎉</h2>
            <p>
              Merci, {userInfo.first_name} {userInfo.last_name}, pour votre
              adhésion.
            </p>
            <p>
              Un email de confirmation a été envoyé à{" "}
              <strong>{userInfo.email}</strong>.
            </p>
            <button onClick={() => setIsModalOpen(false)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
